"use strict";

import Vue from 'vue';
import axios from "axios";
import qs from "qs"; // Import qs for custom params serialization
import { migrateCatalogEntryFormatFilter } from "@/utils/apiFilterParams";
import { trackApiWrites } from "@/utils/apiWriteEpoch";
import { handleUnauthorizedSession, isUnauthorizedError } from "@/utils/unauthorizedSession";

// Store will be set after initialization
let performanceStore = null;

// Function to set the store (called from main.js after store initialization)
export function setPerformanceStore(store) {
    performanceStore = store;
}

// Django session writes use the same CSRF cookie/header for both Axios clients.
axios.defaults.xsrfCookieName = "csrftoken";
axios.defaults.xsrfHeaderName = "X-CSRFToken";

// Base configuration for Axios
const axiosConfig = {
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'comma' }),
    // Disable caching for API calls
    headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
    }
};


const apiClient = axios.create(axiosConfig);

// Some stores call the bare axios instance: track both so the write epoch
// reflects every API mutation, whichever client issued it.
trackApiWrites(apiClient);
trackApiWrites(axios);

function isCatalogEntryListRequest(url) {
    if (!url || typeof url !== "string") {
        return false;
    }
    const path = url.split("?")[0];
    return (
        path.includes("/api/catalogentries") ||
        path.includes("/api/productcatalogentries")
    );
}

// Request interceptor to add cache-busting parameters
apiClient.interceptors.request.use(
    config => {
        if (config.params && isCatalogEntryListRequest(config.url)) {
            config.params = migrateCatalogEntryFormatFilter(config.params);
        }
        // Add timestamp to prevent caching
        if (config.url && config.url.includes('/api/')) {
            const separator = config.url.includes('?') ? '&' : '?';
            config.url += `${separator}_t=${Date.now()}`;
        }
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

let refreshTimeout;

// Function to set or reset the refresh timeout
const setRefreshTimeout = () => {
  clearTimeout(refreshTimeout);
  refreshTimeout = setTimeout(() => {
    window.location.reload(); // Refresh the page after 4 hours of inactivity
  }, 4 * 60 * 60 * 1000); // 4 hours in milliseconds
};

// Response interceptor to reset the timeout on each successful response
apiClient.interceptors.response.use(
  response => {
    setRefreshTimeout(); // Reset the timeout on each successful response
    // Extract performance metrics from headers (only in development)
    if (process.env.NODE_ENV === 'development' && response.headers) {
      const queryCount = response.headers['x-query-count'];
      const queryTime = response.headers['x-query-time'];
      const renderingTime = response.headers['x-rendering-time'];
      const requestTime = response.headers['x-request-time'];
      const requestId = response.headers['x-request-id'];
      const queryComplexity = response.headers['x-query-complexity'];
      const queryTotalComplexity = response.headers['x-query-total-complexity'];
      
      if (queryCount !== undefined || queryTime !== undefined || requestTime !== undefined) {
        // Dispatch to Vuex store if available
        if (performanceStore) {
          // Store request details for replay capability
          const requestConfig = response.config;
          
          // Clean URL: remove cache-busting timestamp parameter
          let cleanUrl = requestConfig.url;
          if (cleanUrl.includes('_t=')) {
            cleanUrl = cleanUrl.replace(/[?&]_t=\d+/, '').replace(/\?$/, '');
          }
          
          // Clean params: remove cache-busting timestamp
          const cleanParams = { ...requestConfig.params };
          if (cleanParams._t) {
            delete cleanParams._t;
          }
          
          // Store only essential headers (exclude performance headers)
          const essentialHeaders = {};
          if (requestConfig.headers) {
            // Copy only relevant headers, exclude performance monitoring headers
            const excludeHeaders = ['x-request-id', 'x-query-count', 'x-query-time', 'x-rendering-time', 'x-request-time', 'x-query-complexity', 'x-query-total-complexity'];
            Object.keys(requestConfig.headers).forEach(key => {
              const lowerKey = key.toLowerCase();
              // Include headers that are not performance headers
              // Include important custom headers like CSRF token and user info
              if (!excludeHeaders.includes(lowerKey)) {
                // Include standard headers and important custom headers
                if (!lowerKey.startsWith('x-') || 
                    lowerKey === 'x-csrftoken' || 
                    lowerKey === 'x-userinfo' ||
                    lowerKey.startsWith('authorization') ||
                    lowerKey.startsWith('content-type')) {
                  essentialHeaders[key] = requestConfig.headers[key];
                }
              }
            });
          }
          
          const replayData = {
            method: requestConfig.method.toUpperCase(),
            url: cleanUrl,
            data: requestConfig.data,
            params: Object.keys(cleanParams).length > 0 ? cleanParams : undefined,
            headers: Object.keys(essentialHeaders).length > 0 ? essentialHeaders : undefined,
          };
          
          performanceStore.dispatch('performance/addMetric', {
            url: response.config.url,
            method: response.config.method,
            queryCount: queryCount ? parseInt(queryCount, 10) : null,
            queryTime: queryTime ? parseFloat(queryTime) : null,
            renderingTime: renderingTime ? parseFloat(renderingTime) : null,
            requestTime: requestTime ? parseFloat(requestTime) : null,
            queryComplexity: queryComplexity ? parseFloat(queryComplexity) : null,
            queryTotalComplexity: queryTotalComplexity ? parseFloat(queryTotalComplexity) : null,
            requestId: requestId || null,
            timestamp: Date.now(),
            replayData: replayData,
          });
        }
      }
    }
    
    return response;
  },
  error => {
    if (isUnauthorizedError(error)) {
      return handleUnauthorizedSession();
    }
    return Promise.reject(error);
  }
);
apiClient.CancelToken = axios.CancelToken;
apiClient.isCancel = axios.isCancel;
apiClient.Cancel = axios.Cancel;

export default apiClient;
